// src/api/api.ts
import { fetchAuthSession, signOut } from 'aws-amplify/auth'; // Import signOut
import type { Client } from "@/types/client";
import type { Session } from "@/types/session";
import { S3MultipartUpload } from '@/utils/s3MultipartUpload';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// --- NEW: Custom Error for HTTP responses ---
// This class helps us retain the HTTP status code in our error handling logic.
class HttpError extends Error {
  status: number;
  response: Response;
  data: any;

  constructor(response: Response, data: any, message?: string) {
    super(message || `HTTP error! Status: ${response.status}`);
    this.name = 'HttpError';
    this.status = response.status;
    this.response = response;
    this.data = data;
  }
}

// Interface for query parameters
interface QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  date?: string;
  [key: string]: any;
}


class ApiService {
  // Private method to get caregiver ID from token
  private async getCaregiverIdFromToken(): Promise<string> {
    try {
      console.log('🔐 Getting caregiver ID from token...');
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();


      if (!idToken) {
        console.error('❌ No ID token found in session');
        throw new Error('Not authenticated: ID token missing.');
      }


      console.log('✅ Token found, extracting caregiver ID...');


      const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
      console.log('🎫 Token payload (selected fields):', {
        sub: tokenPayload.sub,
        'cognito:username': tokenPayload['cognito:username'],
        email: tokenPayload.email,
      });


      const caregiver_id = tokenPayload['cognito:username'] || 
                         tokenPayload.sub || 
                         tokenPayload.email ||
                         tokenPayload.username;


      if (!caregiver_id) {
        console.error('❌ No caregiver ID found in token payload:', Object.keys(tokenPayload));
        throw new Error('Caregiver ID not found in token claims. Available fields: ' + Object.keys(tokenPayload).join(', '));
      }


      console.log('✅ Caregiver ID extracted:', caregiver_id);
      return caregiver_id;


    } catch (error) {
      console.error('❌ Error extracting caregiver ID from token:', error);
      throw error;
    }
  }


  // Prepares the RequestInit object with auth headers
  private async prepareRequestInit(options: Partial<RequestInit> = {}): Promise<{requestInit: RequestInit, caregiver_id: string}> {
    try {
      console.log('🔧 Preparing request init...');
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();


      if (!idToken) {
        console.error('❌ No ID token found in session');
        throw new Error('Not authenticated: ID token missing.');
      }


      const caregiver_id = await this.getCaregiverIdFromToken();


      const headers = new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...(options.headers || {}),
      });


      const requestOptions: RequestInit = {
        headers,
        // credentials: 'include',
        ...options,
      };


      console.log('✅ Request init prepared with caregiver ID:', caregiver_id);
      return { requestInit: requestOptions, caregiver_id };


    } catch (error) {
      console.error('❌ Error preparing request init:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown authentication error occurred.');
    }
  }


  // --- MODIFIED: handles the fetch request and response/error processing ---
  private async handleRequest<T>(url: string, requestInit: RequestInit): Promise<T> {
    try {
      console.log(`🌐 Making fetch request to: ${url}`);
      console.log("requestInit (excluding body for security if large):", {
        method: requestInit.method,
        headers: requestInit.headers,
        credentials: requestInit.credentials,
        cache: requestInit.cache,
      });


      const response = await fetch(url, requestInit);


      if (!response.ok) {
        let errorData: any = { message: `HTTP error! Status: ${response.status}` };
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            errorData = await response.json();
          } else {
            errorData.message = await response.text();
          }
        } catch (e) {
          errorData.message = errorData.message || response.statusText || `Request failed with status ${response.status}`;
        }


        console.error('❌ API Request Failed:', {
            status: response.status,
            statusText: response.statusText,
            url: url,
            responseBody: errorData,
        });

        // Throw our custom HttpError to preserve status code
        throw new HttpError(response, errorData, errorData?.message);
      }


      if (response.status === 204) {
        return {} as T;
      }


      try {
        const data: T = await response.json();
        console.log('✅ API Response received',data);
        return data;
      } catch (error) {
        console.error('❌ Failed to parse JSON response:', error);
        throw new Error('Failed to parse server response.');
      }


    } catch (error) {
      console.error('❌ Fetch operation failed:', error);

      // --- NEW: Interceptor logic for 401 Unauthorized ---
      if (error instanceof HttpError && error.status === 401) {
        console.error('🚨 Intercepted 401 Unauthorized. Session expired. Redirecting to login.');
        
        // Use Amplify's signOut to clear the session globally
        await signOut();
        
        // Redirect to login page, preventing a redirect loop
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        // We throw a new error to stop the promise chain of the original call.
        throw new Error('Session expired. Please log in again.');
      }


      if (error instanceof TypeError) {
        console.error('🌐 Network connectivity issue detected');
        throw new Error('Network error: Could not connect to API.');
      }


      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(String(error) || 'An unknown API error occurred');
      }
    }
  }


  async getClients(params?: QueryParams): Promise<Client[]> {
    console.log("👥 Fetching clients with params:", params);
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit, caregiver_id } = await this.prepareRequestInit({ method: 'GET' });
      const url = `${API_BASE_URL}/caregivers/${caregiver_id}/clients`;
      console.log("🔗 Fetching clients from URL:", url);
      
      const response = await this.handleRequest<any>(url, requestInit);
      
      if (response?.success && Array.isArray(response.data)) {
        console.log("✅ Returning client data array:", response.data);
        return response.data;
      } else if (Array.isArray(response)) {
        console.log("✅ Response is already an array:", response);
        return response;
      } else {
        console.warn("⚠️ Unexpected response format for getClients:", response);
        return [];
      }
    } catch (error) {
      console.error('❌ Error in getClients:', error);
      throw error;
    }
  }

  // --- DICTATION METHODS ---
  
  /**
   * Process dictation with template-aware LLM enhancement
   */
  async processDictation(dictationData: {
    template_type: string;
    dictation_notes: string;
    target_section?: string;
    dictation_mode?: string;
    client_id?: string;
    session_id?: string;
    session_title?: string;
  }): Promise<any> {
    console.log('🎯 ApiService.processDictation called with:', dictationData);


    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      console.log('🔐 Getting authentication and caregiver ID...');
      
      const caregiver_id = await this.getCaregiverIdFromToken();
      console.log('✅ Caregiver ID obtained:', caregiver_id);


      const { requestInit } = await this.prepareRequestInit({
        method: 'POST',
        body: JSON.stringify({
          template_type: dictationData.template_type,
          dictation_notes: dictationData.dictation_notes,
          target_section: dictationData.target_section,
          dictation_mode: dictationData.dictation_mode || 'general',
          client_id: dictationData.client_id,
          caregiver_id: caregiver_id,
          session_id: dictationData.session_id,
          session_title: dictationData.session_title
        })
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/dictation-transcribe`;


      const result = await this.handleRequest<any>(url, requestInit);
      console.log('✅ Dictation processing completed successfully');
      
      return result;
    } catch (error) {
      console.error('❌ Error processing dictation:', error);
      throw error;
    }
  }


  /**
   * Create a new session with enhanced dictation data
   */
  async createSessionWithDictation(sessionData: {
    client_id?: string;
    session_title: string;
    session_type: string;
    template_type: string;
    dictation_notes: string;
    enhanced_response?: any;
    sessionDate?: string;
    sessionTime?: string;
  }): Promise<any> {
    console.log('🎯 ApiService.createSessionWithDictation called');


    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      console.log('🔐 Getting authentication and caregiver ID...');
      
      const caregiver_id = await this.getCaregiverIdFromToken();
      console.log('✅ Caregiver ID obtained:', caregiver_id);

      const requestBody = {
        durationMinutes: '45',
        session_title: sessionData.session_title || 'Dictation Session',
        session_type: sessionData.session_type,
        confirmed: true,
        session_status: 'completed',
        client_id: sessionData.client_id || null,
        caregiver_id: caregiver_id,
        template_type: sessionData.template_type,
        dictation_notes: sessionData.dictation_notes,
        enhanced_response: sessionData.enhanced_response,
        created_via: 'dictation_interface',
        processing_timestamp: new Date().toISOString()
      };


      const { requestInit } = await this.prepareRequestInit({
        method: 'POST',
        body: JSON.stringify(requestBody)
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions`;


      console.log('🚀 Creating session with dictation data at:', url);
      console.log('📤 Request payload (truncated dictation_notes):', {
        ...requestBody,
        dictation_notes: `${requestBody.dictation_notes.substring(0, 100)}...`,
      });


      const result = await this.handleRequest<any>(url, requestInit);
      console.log('✅ Session created successfully');
      
      return result;
    } catch (error) {
      console.error('❌ Error creating session with dictation:', error);
      throw error;
    }
  }


  /**
   * Update an existing session with dictation data
   */
  async updateSessionWithDictation(session_id: string, dictationData: {
    template_type: string;
    dictation_notes: string;
    enhanced_response: any;
  }): Promise<any> {
    console.log('🎯 ApiService.updateSessionWithDictation called');


    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const updateData = {
        ...dictationData,
        updated_via: 'dictation_interface',
        last_updated: new Date().toISOString()
      };


      const { requestInit } = await this.prepareRequestInit({
        method: 'PUT',
        body: JSON.stringify(updateData),
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${session_id}`;


      console.log(`🚀 Updating session ${session_id} with dictation data`);


      const result = await this.handleRequest<any>(url, requestInit);
      console.log('✅ Session updated successfully');
      
      return result;
    } catch (error) {
      console.error(`❌ Error updating session ${session_id}:`, error);
      throw error;
    }
  }


  // --- Other existing methods ---
  
  async createClient(clientData: Client): Promise<Client> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit, caregiver_id } = await this.prepareRequestInit();
      const requestBody = {
        ...clientData,
        caregiver_id: caregiver_id
      };


      const requestInitWithBody = {
        ...requestInit,
        method: 'POST',
        body: JSON.stringify(requestBody)
      };


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients`;


      return this.handleRequest<Client>(url, requestInitWithBody);
    } catch (error) {
      console.error('❌ Error in createClient:', error);
      throw error;
    }
  }


  async getClient(client_id: string): Promise<Client> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients/${client_id}`;


      return this.handleRequest<Client>(url, requestInit);
    } catch (error) {
      console.error('❌ Error in getClient:', error);
      throw error;
    }
  }


  async updateClient(client_id: string, clientData: Partial<Client>): Promise<Client> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({
        method: 'PUT',
        body: JSON.stringify(clientData)
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients/${client_id}`;


      return this.handleRequest<Client>(url, requestInit);
    } catch (error) {
      console.error('❌ Error in updateClient:', error);
      throw error;
    }
  }


  async deleteClient(client_id: string): Promise<void> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }   
    if (!client_id) {
      throw new Error("Client ID is required to delete client data");
    }
    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'DELETE' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients/${client_id}`;


      console.log(`Deleting client: ${client_id}`);
      await this.handleRequest<void>(url, requestInit);
    } catch (error) {
      console.error('❌ Error in deleteClient:', error);
      throw error;
    }
  }


  // Session methods
  async createSession(client_id: string, sessionData: Session) {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit, caregiver_id } = await this.prepareRequestInit();
      
      const durationMinutes = '45';
      
      const requestInitWithBody = {
        ...requestInit,
        method: 'POST',
        body: JSON.stringify({
          durationMinutes,
          session_title: sessionData.session_title,
          session_type: sessionData.session_type,
          confirmed: sessionData.confirmed,
          session_status: sessionData.session_status,
          client_id,
          caregiver_id
        })
      };
      
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions`;
      
      return this.handleRequest<any>(url, requestInitWithBody);
    } catch (error) {
      console.error('❌ Error in createSession:', error);
      throw error;
    }
  }


  async getSessions(params: { client_id: string; caregiver_id: string; status?: string }): Promise<any[]> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = new URL(`${baseUrl}/sessions`);
      url.searchParams.append('client_id', params.client_id);
      url.searchParams.append('caregiver_id', params.caregiver_id);
      if (params.status) url.searchParams.append('status', params.status);


      const response = await this.handleRequest<any>(url.toString(), requestInit);
      
      if (response?.success && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];


    } catch (error) {
      console.error('❌ Error in getSessions:', error);
      throw error;
    }
  }


  async getSessionById(session_id: string): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    if (!session_id) {
      throw new Error("Session ID is required to fetch session data");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${session_id}`;


      console.log(`Fetching session data for session: ${session_id}`);
      return await this.handleRequest<any>(url, requestInit);
    } catch (error) {
      console.error(`❌ Error fetching session data for session ${session_id}:`, error);
      throw error;
    }
  }


  async updateSessionData(session_id: string, sessionData: any): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    if (!session_id) {
      throw new Error("Session ID is required to update session data");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({
        method: 'PUT',
        body: JSON.stringify(sessionData),
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${session_id}`;


      console.log(`Updating session data for session: ${session_id}`);
      return await this.handleRequest<any>(url, requestInit);
    } catch (error) {
      console.error(`❌ Error updating session data for session ${session_id}:`, error);
      throw error;
    }
  }


  async deleteSession(session_id: string): Promise<void> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }   
    if (!session_id) {
      throw new Error("Session ID is required to delete session data");
    }
    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'DELETE' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${session_id}`;


      console.log(`Deleting session: ${session_id}`);
      await this.handleRequest<void>(url, requestInit);
    } catch (error) {
      console.error(`❌ Error deleting session ${session_id}:`, error);
      throw error;
    }
  }


  async getClientSessions(client_id: string): Promise<any[]> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients/${client_id}/sessions`;


      console.log(`Fetching sessions for client: ${client_id}`);
      return await this.handleRequest<any[]>(url, requestInit);
    } catch (error) {
      console.error(`❌ Error fetching sessions for client ${client_id}:`, error);
      throw error;
    }
  }


  async uploadAudioToS3(
    signedUrl: string,
    audioBlob: Blob,
    options?: {
      onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void;
      useMultipart?: boolean;
      chunkSize?: number;
    }
  ): Promise<Response> {
    if (!signedUrl) {
      throw new Error("Upload URL is required");
    }


    if (!audioBlob || audioBlob.size === 0) {
      throw new Error("Invalid audio data: The audio recording is empty or corrupted");
    }


    try {
      console.log(`Starting S3 upload of ${audioBlob.size} bytes`);
      console.log(`Blob type: ${audioBlob.type}`);


      // Use simple upload for smaller files
      console.log('Using simple upload for small file');


      // Extract content-type from the blob or default to application/json for transcriptions
      const contentType = audioBlob.type || 'application/json';
      console.log(`Using Content-Type: ${contentType}`);
      const arrayBuffer = await audioBlob.arrayBuffer();


        const uploadOptions = {
          method: 'PUT',
          body: arrayBuffer,
          headers: {
            'Content-Type': contentType, // Use the actual content type of the blob
          }
        };


        const response = await fetch(signedUrl, uploadOptions);


        if (response.ok) {
          console.log(`✅ Upload successful! Status: ${response.status}`);
        } else {
          const errorText = await response.clone().text();
          console.error(`❌ S3 upload failed with status: ${response.status}`);
          console.error('Error response:', errorText);
        }


        // Call progress callback with 100% if provided
        if (response.ok && options?.onProgress) {
          options.onProgress({
            loaded: audioBlob.size,
            total: audioBlob.size,
            percentage: 100
          });
        }


        return response;
      
    } catch (error) {
      console.error('❌ Error in S3 upload process:', error);


      // Attempt to save locally as backup
      if (audioBlob && audioBlob.size > 0) {
        try {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const saved = await S3MultipartUpload.saveAudioLocally(
            audioBlob,
            `consultation-backup-${timestamp}.webm`
          );
          if (saved) {
            console.log('✅ Audio saved locally as backup');
          }
        } catch (backupError) {
          console.error('Failed to save backup:', backupError);
        }
      }


      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Network error during upload. Check your internet connection. Audio has been saved locally.');
      }


      if (error instanceof Error) {
        throw new Error(`S3 upload error: ${error.message}`);
      } else {
        throw new Error(`S3 upload error: ${String(error)}`);
      }
    }
  }


  async uploadJsonToS3(signedUrl: string | any, data: object | string): Promise<Response> {
  // Handle the nested response structure
  let actualUrl: string;
  
  if (typeof signedUrl === 'object' && signedUrl !== null) {
    // Handle nested response like {success: true, data: {uploadUrl: '...'}}
    if (signedUrl.data?.uploadUrl) {
      actualUrl = signedUrl.data.uploadUrl;
    } else if (signedUrl.uploadUrl) {
      actualUrl = signedUrl.uploadUrl;
    } else {
      throw new Error("No valid upload URL found in response object");
    }
  } else if (typeof signedUrl === 'string') {
    actualUrl = signedUrl;
  } else {
    throw new Error("Invalid upload URL format");
  }
  
  if (!actualUrl) throw new Error("Upload URL is required");
  
  console.log("Using upload URL:", actualUrl);


  // Normalize to JSON Blob
  let body: Blob;
  if (data instanceof Blob) {
    if ((data.type || '').toLowerCase() !== 'application/json') {
      throw new Error(`JSON-only upload requires Blob type application/json, got '\${data.type || 'unknown'}'`);
    }
    if (data.size === 0) throw new Error("Invalid data: JSON Blob is empty");
    body = data;
  } else if (typeof data === 'string') {
    if (!data.trim()) throw new Error("Invalid data: JSON string is empty");
    body = new Blob([data], { type: 'application/json' });
  } else {
    body = new Blob([JSON.stringify(data ?? {})], { type: 'application/json' });
  }


  // IMPORTANT: Don't send Content-Type header with presigned URLs
  // The Content-Type is already included in the presigned URL signature
  // Sending it as a header will cause CORS preflight which S3 doesn't handle
  const resp = await fetch(actualUrl, {
    method: 'PUT',
    body,
  });


  if (!resp.ok) {
    const errorText = await resp.text().catch(() => 'No error details available');
    console.error('S3 upload failed:', {
      status: resp.status,
      statusText: resp.statusText,
      error: errorText
    });
  }


  return resp;
}
  
  async getSessionsByCaregiver(): Promise<any[]> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }
    let caregiver_id: string | undefined;
    try {
      caregiver_id = await this.getCaregiverIdFromToken();
      if (!caregiver_id) {
        throw new Error("Caregiver ID is required to fetch sessions");
      }
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions?caregiver_id=${encodeURIComponent(caregiver_id)}`;
      console.log(`Fetching sessions for caregiver: ${caregiver_id}`);
      
      const response = await this.handleRequest<any>(url, requestInit);
      if (response?.success && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.error(`❌ Error fetching sessions for caregiver ${caregiver_id ?? 'unknown'}:`, error);
      throw error;
    }
  }


  async getLatestClientReport(client_id: string): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }
    if (!client_id) {
      throw new Error("Client ID is required to fetch the latest report");
    }
    try {
      const { requestInit } = await this.prepareRequestInit({ method: 'GET' });
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/clients/${client_id}/reports/latest`;
      console.log(`Fetching latest report for client: ${client_id}`);
      return await this.handleRequest<any>(url, requestInit);
    } catch (error) {
      console.error(`❌ Error fetching latest report for client ${client_id}:`, error);
      throw error;
    }
  }
  // Add these methods to your ApiService class in api.ts


  /**
   * Get a presigned URL specifically for audio uploads
   * This is the main method for audio file uploads
   */
  async getSessionAudioUploadUrl(sessionId: string, customParams?: Partial<{
    fileName?: string;
    contentType?: string;
    duration?: number;
  }>): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }


    if (!sessionId) {
      throw new Error("Session ID is required to get audio upload URL");
    }


    // Build request body for audio upload
    // Backend expects FLAC format for AWS Transcribe and will change extension
    const requestBody = {
      fileName: customParams?.fileName || `recording_${Date.now()}.webm`,
      contentType: customParams?.contentType || 'audio/flac',  // Use what's provided or default to flac
      uploadType: 'audio',
      actualFormat: 'webm',  // What we're actually sending
      targetFormat: 'flac',  // What backend will convert to
      needsConversion: true, // Tell backend it needs conversion
      duration: customParams?.duration
    };


    try {
      console.log(`📤 Requesting audio upload URL for session ${sessionId}`);
      console.log('🎙️ Audio upload parameters:', requestBody);


      const { requestInit } = await this.prepareRequestInit({
        method: 'POST',
        body: JSON.stringify(requestBody)
      });


      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${sessionId}/upload-content`;


      const response = await this.handleRequest<any>(url, requestInit);


      console.log('✅ Audio upload URL received:', {
        uploadUrl: response.uploadUrl ? 'URL received' : 'No URL in response',
        fileKey: response.fileKey,
        uploadType: response.uploadType
      });


      return response;
    } catch (error) {
      console.error(`❌ Error getting audio upload URL for session ${sessionId}:`, error);
      throw error;
    }
  }


  /**
   * Get a presigned URL for uploading files to S3
   * Can handle both audio and transcription uploads based on parameters
   */
  async getSessionUploadUrl(sessionId: string, uploadParams?: any): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }
    
    if (!sessionId) {
      throw new Error("Session ID is required to get upload URL");
    }


    // Default parameters for audio upload (backward compatibility)
    const defaultParams = {
      fileName: `recording_${Date.now()}.flac`,
      contentType: 'audio/flac',
      uploadType: 'audio',
      actualFormat: 'webm',
      targetFormat: 'flac',
      needsConversion: true
    };
    
    const requestBody = uploadParams || defaultParams;
    
    try {
      console.log(`📤 Requesting upload URL for session ${sessionId} with params:`, requestBody);
      
      const { requestInit } = await this.prepareRequestInit({
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
      
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${sessionId}/upload-content`;
      
      const response = await this.handleRequest<any>(url, requestInit);
      
      console.log('✅ Upload URL received:', {
        uploadType: requestBody.uploadType,
        contentType: requestBody.contentType,
        fileName: requestBody.fileName
      });
      
      return response;
    } catch (error) {
      console.error(`❌ Error getting upload URL for session ${sessionId}:`, error);
      throw error;
    }
  }


  /**
   * Get a presigned URL specifically for transcription uploads
   * This method is specifically for JSON transcript uploads
   */
  async getSessionTranscriptionUploadUrl(sessionId: string, customParams?: Partial<{
    fileName: string;
    transcriptionFormat: string;
    sourceType: string;
  }>): Promise<any> {
    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }
    
    if (!sessionId) {
      throw new Error("Session ID is required to get transcription upload URL");
    }


    // Build the request body for transcription upload
    const requestBody = {
      fileName: customParams?.fileName || `consultation_transcript_${Date.now()}.json`,
      contentType: 'application/json',
      uploadType: 'transcription',  // This tells the backend to use transcription path
      transcriptionFormat: customParams?.transcriptionFormat || 'aws_transcribe',
      sourceType: customParams?.sourceType || 'user_upload'
    };
    
    try {
      console.log(`📤 Requesting transcription upload URL for session ${sessionId}`);
      console.log('📋 Transcription upload parameters:', requestBody);
      
      const { requestInit } = await this.prepareRequestInit({
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
      
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/sessions/${sessionId}/upload-content`;
      
      const response = await this.handleRequest<any>(url, requestInit);
      
      // Log the response for debugging
      console.log('✅ Transcription upload URL received:', {
        uploadUrl: response.uploadUrl ? 'URL received' : 'No URL in response',
        fileKey: response.fileKey,
        uploadType: response.uploadType,
        contentType: response.contentType
      });
      
      // Validate that we got the right type of URL
      if (response.uploadType && response.uploadType !== 'transcription') {
        console.warn(`⚠️ Expected transcription upload URL but got ${response.uploadType}`);
      }
      
      if (response.contentType && !response.contentType.includes('json')) {
        console.warn(`⚠️ Expected application/json content type but got ${response.contentType}`);
      }
      
      return response;
    } catch (error) {
      console.error(`❌ Error getting transcription upload URL for session ${sessionId}:`, error);
      throw error;
    }
  }

  /**
   * Update caregiver profile/preferences
   */
  async updateCaregiverProfile(profileData: {
    soap_documentation_style?: string;
    [key: string]: any;
  }): Promise<any> {
    console.log('🎯 ApiService.updateCaregiverProfile called with:', profileData);

    if (!API_BASE_URL) {
      throw new Error("API_BASE_URL is not configured. Check your environment variables.");
    }

    try {
      const caregiver_id = await this.getCaregiverIdFromToken();
      console.log('✅ Caregiver ID obtained:', caregiver_id);

      const { requestInit } = await this.prepareRequestInit({
        method: 'PUT',
        body: JSON.stringify(profileData)
      });

      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/caregivers/${caregiver_id}`;

      console.log('🚀 Updating caregiver profile at:', url);

      const result = await this.handleRequest<any>(url, requestInit);
      console.log('✅ Caregiver profile updated successfully');

      return result;
    } catch (error) {
      console.error('❌ Error updating caregiver profile:', error);
      throw error;
    }
  }

}

export const apiService = new ApiService();